import { initializeApp } from "firebase/app";
import {
    getAuth,
    signInWithPopup,
    GoogleAuthProvider,
    onAuthStateChanged,
    signOut as firebaseSignOut,
    signInWithEmailAndPassword,
    connectAuthEmulator,
} from "firebase/auth";
import config from "@/config";

const {
    apiKey,
    authDomain,
    databaseURL,
    projectId,
    storageBucket,
    messagingSenderId,
    appId,
    measurementId,
    emulatorUrl,
} = config.authentication.firebase;

const provider = new GoogleAuthProvider();

export enum LoginStatus {
    UNAUTHENTICATED = "unauthenticated",
    AUTHENTICATED = "authenticated",
}

const app = initializeApp({
    apiKey,
    authDomain,
    databaseURL,
    projectId,
    storageBucket,
    messagingSenderId,
    appId,
    measurementId,
});
const firebaseAuth = getAuth(app);
if (emulatorUrl) {
    connectAuthEmulator(firebaseAuth, emulatorUrl, { disableWarnings: true });
}
firebaseAuth.useDeviceLanguage();

// firebaseAuth.currentUser is configured asynchronously, which can cause
// an 'unauthenticated' event even though the user could actually be
// authenticated. Luckily firebase always emits an event after initializing,
// which we can abuse to wait for a bit, so that we can always be sure that
// firebaseAuth.currentUser returns a user when possible.
const isFirebaseInitialized = new Promise((resolve) => {
    onAuthStateChanged(firebaseAuth, resolve);
});

export async function signIn(email?: string, password?: string) {
    if (email !== undefined && password !== undefined) {
        return signInWithEmailAndPassword(firebaseAuth, email, password);
    }

    return signInWithPopup(firebaseAuth, provider);
}

export async function signOut() {
    return await firebaseSignOut(firebaseAuth);
}

export async function hasToken() {
    return !!(await getToken(true));
}

// Updates the status and dispatches an event in case it has changed.
export function updateStatus(newStatus: LoginStatus) {
    // Don't emit event again when status was already authenticated.
    if (newStatus === LoginStatus.AUTHENTICATED && status === newStatus) {
        return;
    }

    status = newStatus;
    events.dispatchEvent(new CustomEvent(newStatus));
}

// Retrieve the token that the API calls to the backend need.
export async function getToken(
    skipStatusUpdate = false
): Promise<string | undefined> {
    await isFirebaseInitialized;
    const user = firebaseAuth.currentUser;
    if (user) {
        const token = await user.getIdToken();
        if (!skipStatusUpdate) {
            updateStatus(LoginStatus.AUTHENTICATED);
        }
        return token;
    } else {
        if (!skipStatusUpdate) {
            updateStatus(LoginStatus.UNAUTHENTICATED);
        }
    }
}

onAuthStateChanged(firebaseAuth, async (user) => {
    if (user) {
        updateStatus(LoginStatus.AUTHENTICATED);
    } else {
        updateStatus(LoginStatus.UNAUTHENTICATED);
    }
});

export let status = LoginStatus.UNAUTHENTICATED;
export const events = new EventTarget();
